import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DOCUMENT, SlicePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { ThemeService } from '@app/core/layout/theme.service';
import { APP_HEADER_I18N } from './app-header.i18n';
import { environment } from 'src/environments/environment';
import { DatabaseCacheService } from '../../../data/cache/database-cache.service';

/** Language configuration */
interface LangConfig {
  code: string;
  label: string;
  flag: string; // path to SVG asset
}

/** Available languages */
const LANGUAGES: LangConfig[] = [
  { code: 'ja', label: '日本語', flag: 'assets/flags/ja.svg' },
  { code: 'en', label: 'English', flag: 'assets/flags/en.svg' },
  { code: 'tw', label: '繁體中文', flag: 'assets/flags/zh-tw.svg' },
];

/** Development port mapping for languages */
const DEV_LANG_PORTS: Record<string, number> = {
  ja: 4200,
  en: 4201,
  tw: 4202,
};

/** Hosts that should display the oishien brand logo instead of the GOKURI default */
const OISHIEN_HOSTS = new Set<string>([
  'oishien.app.gokuri.com',
  'efrgaevfbgs.app.gokuri.com',
]);

/** Brand-specific default logo (used to replace the GOKURI default logo per host) */
const OISHIEN_LOGO_URL = 'assets/oishien_logo_bk.svg';

@Component({
  selector: 'app-header',
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SlicePipe, MatButtonModule, MatIconModule, MatMenuModule, MatDividerModule],
  host: {
    '[class.sidenav-collapsed]': 'sidenavCollapsed()',
  },
})
export class AppHeaderComponent {
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly document = inject(DOCUMENT);
  public readonly common = inject(DatabaseCacheService);

  /** Current language detected from URL */
  readonly currentLang = signal(this.detectCurrentLang());

  /** All available languages */
  readonly languages = LANGUAGES;

  /** Current language's flag path */
  readonly currentFlag = computed(() => {
    const lang = LANGUAGES.find(l => l.code === this.currentLang());
    return lang?.flag ?? LANGUAGES[0].flag;
  });

  /** Current language label */
  readonly currentLangLabel = computed(() => {
    const lang = LANGUAGES.find(l => l.code === this.currentLang());
    return lang?.label ?? LANGUAGES[0].label;
  });

  // Inputs
  readonly logoUrl = input<string>('');

  /**
   * Logo to actually render in the header.
   * On oishien-branded hosts, the default GOKURI logo is swapped for the oishien
   * logo. A custom section-group logo (anything that is not the GOKURI default)
   * is left untouched.
   */
  readonly displayLogoUrl = computed(() => {
    const logo = this.logoUrl();
    if (this.isOishienHost() && this.isDefaultGokuriLogo(logo)) {
      return OISHIEN_LOGO_URL;
    }
    return logo;
  });
  readonly workspaceName = input<string>('');
  readonly userName = input<string>('');
  readonly userRole = input<string>('');
  readonly showHamburger = input(true);
  readonly showHelp = input(true);
  readonly showFaq = input(false);
  readonly sidenavCollapsed = input(false);

  // Outputs
  readonly hamburgerClick = output<void>();
  readonly helpClick = output<void>();
  readonly logoutClick = output<void>();
  readonly themeToggle = output<void>();

  // Theme computed signals
  readonly themeIcon = computed(() => {
    const mode = this.themeService.mode();
    switch (mode) {
      case 'light':
        return 'light_mode';
      case 'dark':
        return 'dark_mode';
      default:
        return 'contrast';
    }
  });

  readonly themeLabel = computed(() => {
    const mode = this.themeService.mode();
    switch (mode) {
      case 'light':
        return APP_HEADER_I18N.theme.lightMode;
      case 'dark':
        return APP_HEADER_I18N.theme.darkMode;
      default:
        return APP_HEADER_I18N.theme.systemSetting;
    }
  });

  openFaq(): void {
    window.open('https://www.gokuri.com/faq', '_blank', 'noopener,noreferrer');
  }

  readonly isDarkMode = computed(() => this.themeService.mode() === 'dark');

  /** Show debug-only menu items (license page etc.) */
  readonly isDebug = !environment.production;

  /**
   * Detect current language from URL
   * Production: https://app.gokuri.com/ja/#/home/... -> 'ja'
   * Development: localhost:4200 -> 'ja', localhost:4201 -> 'en', localhost:4202 -> 'tw'
   */
  private detectCurrentLang(): string {
    const window = this.document.defaultView;
    if (!window) return 'ja';

    const { hostname, port, pathname } = window.location;

    // Development: detect by port (localhost, 127.0.0.1, or any host with dev port)
    if (this.isDevEnvironment(hostname, port)) {
      const portNum = parseInt(port, 10);
      for (const [lang, langPort] of Object.entries(DEV_LANG_PORTS)) {
        if (portNum === langPort) return lang;
      }
      return 'ja'; // Default for dev
    }

    // Production: detect from path (e.g., /ja/#/home/...)
    const langMatch = pathname.match(/^\/(ja|en|tw)\//);
    return langMatch ? langMatch[1] : 'ja';
  }

  /** Whether the current host should use the oishien brand logo */
  private isOishienHost(): boolean {
    const hostname = this.document.defaultView?.location.hostname ?? '';
    return OISHIEN_HOSTS.has(hostname);
  }

  /** Whether the given logo URL is the default GOKURI brand logo (no custom logo set) */
  private isDefaultGokuriLogo(logo: string): boolean {
    return logo === '' || logo.includes('gokuri_logo');
  }

  /** Check if running in development environment by hostname or port */
  private isDevEnvironment(hostname: string, port: string): boolean {
    if (environment.production) return false;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
    // Any host with a known dev port (e.g., Docker forwarded ports)
    const portNum = parseInt(port, 10);
    return Object.values(DEV_LANG_PORTS).includes(portNum);
  }

  /**
   * Switch language by navigating to the appropriate URL
   * Production: changes path segment (/ja/ -> /en/)
   * Development: changes port (4200 -> 4201)
   */
  switchLanguage(targetLang: string): void {
    if (targetLang === this.currentLang()) return;

    const window = this.document.defaultView;
    if (!window) return;

    const { hostname, port, protocol, pathname, hash } = window.location;

    // Development: switch port (localhost, 127.0.0.1, or any host with dev port)
    if (this.isDevEnvironment(hostname, port)) {
      const targetPort = DEV_LANG_PORTS[targetLang] ?? 4200;
      window.location.href = `${protocol}//${hostname}:${targetPort}${pathname}${hash}`;
      return;
    }

    // Production: switch path segment
    const currentLangPattern = /^\/(ja|en|tw)\//;
    const newPathname = pathname.replace(currentLangPattern, `/${targetLang}/`);

    // If no language in path, prepend it
    const finalPathname = currentLangPattern.test(pathname)
      ? newPathname
      : `/${targetLang}${pathname}`;

    window.location.href = `${protocol}//${hostname}${finalPathname}${hash}`;
  }

  /** Navigate to a legal page within the current nav frame */
  navigateToPage(page: string): void {
    const url = this.router.url;
    const segments = url.split('/').filter(Boolean);

    if (segments[0] === 'home' && segments.length >= 2) {
      this.router.navigate(['/home', segments[1], page]);
    } else if (segments[0] === 'section-admin' && segments.length >= 2 && segments[1] !== page) {
      this.router.navigate(['/section-admin', segments[1], page]);
    } else if (segments[0] === 'section-group-admin' && segments.length >= 2 && segments[1] !== page) {
      this.router.navigate(['/section-group-admin', segments[1], page]);
    } else if (segments[0] === 'system-admin') {
      this.router.navigate(['/system-admin', page]);
    } else {
      this.router.navigate(['/', page]);
    }
  }
}
